import i18next from 'i18next'
import React from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { getFSClassName } from '../utils/getClassNameFromStyle'

// ==========================================================================
// FORM CONTROLS & BUTTONS
// ==========================================================================

export type FormItemBaseProps = {
  controlId: string
  disabled?: boolean
  errorMessage?: string
  errorId?: string
  extraGroupClasses?: string
  fieldMessage?: string
  invalid?: boolean
  label: string | JSX.Element
  readonly?: boolean
  required: boolean
}

export type FormControlProps = {
  type?: string
  rows?: number
  as?: React.ElementType
  value?: string
  onChange?: (_: string) => void
  onBlur?: (_: string) => void
  onFocus?: (_: string) => void
  children?: JSX.Element | Array<JSX.Element>
  placeholder?: string
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
  showPasswordToggle?: PasswordToggleTypes
  passwordScore?: PasswordScore
  maxLength?: number
  min?: string
  max?: string
  step?: string
} & FormItemBaseProps

export type PasswordScore = 'strong' | 'almost good' | 'weak'

type PasswordToggleTypes = {
  isChecked: boolean
  label: string
  title?: string
  onChange: (_: boolean) => void
}

type FormSelectProps = {
  value: string
  onChange: (_: string) => void
  onBlur?: (_: string) => void
  options: { label: string; value: string; disabled?: boolean }[]
} & FormItemBaseProps

type FormUploadProps = {
  accept?: string
  multiple?: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
} & FormItemBaseProps

type FormDataSetProps = {
  value?: string
  onChange: (_: string) => void
  children?: JSX.Element | Array<JSX.Element>
  placeholder?: string
} & FormItemBaseProps

type FormBooleanFieldsetItemProps = {
  label: string | JSX.Element
  controlId: string
  checked: boolean
  onChange: (_: boolean) => void
  extraJSX?: React.ReactNode
}

type FormBooleanFieldsetProps = {
  legend?: string | JSX.Element
  name: string
  required: boolean
  readonly?: boolean
  disabled?: boolean
  fieldMessage?: string
  items: FormBooleanFieldsetItemProps[]
  errorMessage?: string
  style?: 'button' | 'input'
  legendClassName?: string,
  direction?: 'row' | 'column'
  boxed?: boolean
  errorId?: string
  invalid?: boolean
  onBlur?: () => void
  extraGroupClasses?: string
}

type FormSwitchProps = {
  label: string | JSX.Element
  controlId: string
  checked: boolean
  required: boolean
  disabled?: boolean
  fieldMessage?: string
  onChange: (_: boolean) => void
  extraGroupClasses?: string
}

type ClickableButtonProps = {
  kind: 'button'
  onClick: () => void
  type?: 'button' | 'submit'
}

type ClickableLinkProps = {
  kind: 'link'
  to: string
  target?: string
  onClick?: () => void
}

type ButtonProps = {
  label: string | JSX.Element
  disabled: boolean
} & (ClickableButtonProps | ClickableLinkProps)

const fieldErrorClassName = getFSClassName('formFieldErrorClassName')

export const FormControls = {
  ShowIf: (props: { condition: boolean; children: JSX.Element }) => (props.condition ? <>{props.children}</> : <></>),
  SubmittingSpinner: () => (
    <Spinner animation="border" role="status" as="span" size="sm">
      <span className="sr-text">{i18next.t('global:Submitting')}</span>
    </Spinner>
  ),
  SubmittingSpinnerOrLabel: (props: { showLoader: boolean; label: string }) =>
    props.showLoader ? <FormControls.SubmittingSpinner /> : <>{props.label}</>,
  RequiredLabel: (props: { required: boolean }) =>
    props.required ? (
      <span className="form-required">
        * <span className="sr-text">{i18next.t('General:Required')}</span>
      </span>
    ) : (
      <></>
    ),
  FormErrorText: (props: { errorMessageId: string; errorMessage?: string }) =>
    props.errorMessage ? (
      <Form.Control.Feedback
        as="p"
        role="alert"
        type="invalid"
        id={props.errorMessageId}
        className={getFSClassName('formFieldFeedbackInvalidClassName')}
      >
        {props.errorMessage}
      </Form.Control.Feedback>
    ) : (
      <></>
    ),
  FormFieldText: (props: { fieldMessageId: string; fieldMessage: string }) => (
    <Form.Text as="p" id={props.fieldMessageId} className={getFSClassName('formFieldFeedbackClassName')}>
      {props.fieldMessage}
    </Form.Text>
  ),
  FormItem: (props: FormControlProps): JSX.Element => {
    const {
      as,
      children,
      controlId,
      disabled,
      errorId,
      errorMessage,
      fieldMessage,
      inputMode,
      invalid,
      max,
      maxLength,
      min,
      step,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      readonly,
      required,
      rows,
      type,
      value,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Control
        type={type}
        as={as}
        rows={rows}
        value={value || ''}
        placeholder={placeholder}
        inputMode={inputMode || 'text'}
        readOnly={readonly}
        disabled={disabled}
        aria-required={required}
        isInvalid={isInvalid ? true : undefined}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
        onChange={onChange == undefined ? undefined : (e) => onChange(e.currentTarget.value)}
        onBlur={(e) => {
          if (onBlur) onBlur(e.currentTarget.value)
          if (e.currentTarget.value == '') {
            e.currentTarget
              ?.closest(`.${getFSClassName('formGroupClassName')}`)
              ?.classList.remove(getFSClassName('formGroupFocusedClassName'))
          }
        }}
        onFocus={(e) => {
          if (onFocus) onFocus(e.currentTarget.value)
          if (e.currentTarget.value == '') {
            e.currentTarget
              ?.closest(`.${getFSClassName('formGroupClassName')}`)
              ?.classList.add(getFSClassName('formGroupFocusedClassName'))
          }
        }}
        className={getFSClassName('formInputClassName')}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
      >
        {children}
      </Form.Control>
    )
  },
  FormItemWithID: (props: FormControlProps): JSX.Element => {
    const {
      as,
      controlId,
      children,
      fieldMessage,
      inputMode,
      maxLength,
      onBlur,
      onChange,
      placeholder,
      required,
      disabled,
      readonly,
      rows,
      type,
      errorMessage,
      value,
    } = props
    const isInvalid = errorMessage != undefined
    const errorMessageId = `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Control
        id={controlId}
        type={type}
        as={as}
        rows={rows}
        value={value || ''}
        placeholder={placeholder}
        inputMode={inputMode || 'text'}
        readOnly={readonly}
        disabled={disabled}
        aria-required={required}
        isInvalid={isInvalid ? true : undefined}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
        onChange={onChange ? (e) => onChange(e.currentTarget.value) : undefined}
        onBlur={onBlur ? (e) => onBlur(e.currentTarget.value) : undefined}
        className={getFSClassName('formInputClassName')}
        maxLength={maxLength}
      >
        {children}
      </Form.Control>
    )
  },
  FormItemInGroup: (props: FormControlProps): JSX.Element => {
    const {
      controlId,
      errorMessage,
      errorId,
      extraGroupClasses,
      fieldMessage,
      invalid,
      label,
      passwordScore,
      showPasswordToggle,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    const isPasswordControl = controlId.toLowerCase().includes('password')
    const hasPasswordStrengthBar = passwordScore != undefined
    return (
      <Form.Group
        controlId={controlId}
        className={`${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupInputClassName')} ${
          extraGroupClasses || ''
        }`}
      >
        <Form.Label className={getFSClassName('formLabelClassName')}>
          {label}
          {/* <FormControls.OptionalLabel {...props} /> */}
          <FormControls.RequiredLabel required={props.required} />
        </Form.Label>

        {isPasswordControl && showPasswordToggle ? (
          // When input has a `showPasswordToggle`, wrap it in a div (otherwise showPasswordToggle positioning conflicts with the `Form.Control.Feedback` positioning)
          // Add error classes to this div, because `Form.Control.Feedback` is shown through CSS sibling selector (.is-invalid ~ .invalid-feedback)
          <div className={`fs__input-wrapper${isInvalid && !hasPasswordStrengthBar ? ' is-invalid' : ''}`}>
            <FormControls.FormItem {...props} invalid={isInvalid} />
            <FormControls.PasswordToggle {...props} />
          </div>
        ) : (
          <FormControls.FormItem {...props} invalid={isInvalid} />
        )}

        {/* Validation feedback & Field message */}
        {errorMessage && !hasPasswordStrengthBar && (
          <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />
        )}

        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}

        {/* Password strength bar */}
        {hasPasswordStrengthBar && props.passwordScore && (
          <FormControls.PasswordStrengthBar
            password={props.value}
            passwordScore={props.passwordScore}
            errorMessageId={errorMessageId}
          />
        )}
      </Form.Group>
    )
  },
  FormSelectInGroup: (props: FormSelectProps): JSX.Element => {
    const {
      controlId,
      disabled,
      errorMessage,
      errorId,
      extraGroupClasses,
      fieldMessage,
      invalid,
      label,
      onBlur,
      onChange,
      options,
      readonly,
      required,
      value,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Group
        controlId={controlId}
        className={`${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupSelectClassName')}  ${
          extraGroupClasses || ''
        }`}
      >
        <Form.Label className={getFSClassName('formLabelClassName')}>
          {label}
          <FormControls.RequiredLabel {...props} />
        </Form.Label>

        <Form.Select
          value={value}
          disabled={disabled}
          aria-readonly={readonly}
          aria-required={required}
          isInvalid={isInvalid ? true : undefined}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
          onBlur={onBlur ? (e) => onBlur(e.currentTarget.value) : undefined}
          className={getFSClassName('formSelectClassName')}
        >
          {options.map((option) => (
            <option key={option.label} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </Form.Select>

        {/* Validation feedback & Field message */}
        {errorMessage && <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />}

        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </Form.Group>
    )
  },
  FormUploadInGroup: (props: FormUploadProps): JSX.Element => {
    const {
      accept,
      controlId,
      disabled,
      errorMessage,
      errorId,
      extraGroupClasses,
      fieldMessage,
      invalid,
      label,
      multiple,
      onChange,
      readonly,
      required,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Group
        controlId={controlId}
        className={`${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupSelectClassName')} ${
          extraGroupClasses || ''
        }`}
      >
        <Form.Label className={getFSClassName('formLabelClassName')}>
          {label}
          <FormControls.RequiredLabel {...props} />
        </Form.Label>

        <Form.Control
          type="file"
          readOnly={readonly}
          disabled={disabled}
          aria-required={required}
          isInvalid={isInvalid}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
          onChange={onChange}
          className={getFSClassName('formInputClassName')}
          accept={accept}
          multiple={multiple}
        />

        {/* Validation feedback & Field message */}
        {errorMessage && <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />}

        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </Form.Group>
    )
  },
  FormDataListInGroup: (props: FormDataSetProps): JSX.Element => {
    const {
      children,
      controlId,
      disabled,
      errorMessage,
      errorId,
      extraGroupClasses,
      fieldMessage,
      invalid,
      label,
      onChange,
      placeholder,
      readonly,
      required,
      value,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${controlId}-form-error`
    const fieldMessageId = `a11y-${controlId}-form-message`
    const inputId = `dl-input-${controlId}`
    const datalistId = `dl-datalist-${controlId}`
    return (
      <Form.Group className={`${getFSClassName('formGroupClassName')}  ${extraGroupClasses || ''}`}>
        <Form.Label htmlFor={inputId} className={getFSClassName('formLabelClassName')}>
          {label}
          <FormControls.RequiredLabel {...props} />
        </Form.Label>

        <input
          id={inputId}
          list={datalistId}
          value={value || ''}
          disabled={disabled}
          readOnly={readonly}
          placeholder={placeholder}
          aria-required={required}
          aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
          className={
            'form-select ' +
            getFSClassName('formDataListClassName') +
            (isInvalid ? ` ${getFSClassName('formFieldErrorClassName')}` : '')
          }
          onFocus={() => onChange('')}
        />

        <datalist id={datalistId}>{children}</datalist>

        {/* Validation feedback & Field message */}
        {errorMessage && <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />}

        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </Form.Group>
    )
  },
  FormCheckInGroup: (props: FormBooleanFieldsetProps): JSX.Element => {
    const {
      disabled,
      errorMessage,
      errorId,
      extraGroupClasses,
      fieldMessage,
      invalid,
      items,
      legend,
      name,
      onBlur,
      readonly,
    } = props
    const checkboxesDivRef = React.useRef<HTMLDivElement>(null)
    const checkboxesFieldsetRef = React.useRef<HTMLFieldSetElement>(null)
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${name}-form-error`
    const fieldMessageId = `a11y-${name}-form-message`
    const FormCheck = (item: FormBooleanFieldsetItemProps) => (
      <Form.Check className={isInvalid ? fieldErrorClassName : ''} key={item.controlId}>
        <Form.Check.Input
          type="checkbox"
          name={name}
          id={item.controlId}
          disabled={disabled}
          readOnly={readonly}
          checked={item.checked}
          isInvalid={isInvalid ? true : undefined}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => item.onChange(e.currentTarget.checked)}
          className={getFSClassName('formBooleanInputClassName')}
          onBlur={() => {
            if (onBlur) {
              if (checkboxesDivRef.current && !checkboxesDivRef.current.matches(':focus-within')) onBlur()
              if (checkboxesFieldsetRef.current && !checkboxesFieldsetRef.current.matches(':focus-within')) onBlur()
            }
          }}
        />

        {typeof item.label == 'string' ? (
          <Form.Check.Label
            className={getFSClassName('formBooleanLabelClassName')}
            htmlFor={item.controlId}
            dangerouslySetInnerHTML={{ __html: item.label }}
          />
        ) : (
          <Form.Check.Label className={getFSClassName('formBooleanLabelClassName')} htmlFor={item.controlId}>
            {item.label}
            <FormControls.RequiredLabel {...props} />
          </Form.Check.Label>
        )}
      </Form.Check>
    )
    return (
      <>
        {/* Render single checkbox */}
        {items.length && items.length == 1 ? (
          items.map((item) => {
            const { controlId } = item
            return (
              <Form.Group
                ref={checkboxesDivRef}
                key={`check-${controlId}`}
                className={`${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupCheckboxClassName')}`}
              >
                {legend && (
                  <Form.Label className={getFSClassName('formLabelClassName')}>
                    {legend}
                    <FormControls.RequiredLabel {...props} />
                  </Form.Label>
                )}

                <FormCheck {...item} />

                {/* Validation feedback & Field message */}
                {errorMessage && (
                  <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />
                )}

                {fieldMessage && (
                  <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />
                )}
              </Form.Group>
            )
          })
        ) : (
          // Render multiple checkboxes
          <Form.Group
            ref={checkboxesFieldsetRef}
            as="fieldset"
            className={`${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupRadioClassName')} ${
              extraGroupClasses || ''
            }`}
          >
            {legend && (
              <Form.Label as="legend" className={getFSClassName('formLabelClassName')}>
                {legend}
                <FormControls.RequiredLabel {...props} />
              </Form.Label>
            )}

            {items.length > 0 && items.map((item) => <FormCheck key={`check-${item.controlId}`} {...item} />)}

            {/* Validation feedback & Field message */}
            {errorMessage != undefined && (
              <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />
            )}

            {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
          </Form.Group>
        )}
      </>
    )
  },
  FormRadioInGroup: (props: FormBooleanFieldsetProps): JSX.Element => {
    const {
      disabled,
      errorId,
      errorMessage,
      extraGroupClasses,
      fieldMessage,
      invalid,
      items,
      legend,
      name,
      onBlur,
      readonly,
      required,
      style,
      direction,
      boxed,
    } = props
    const isInvalid = invalid || errorMessage != undefined
    const errorMessageId = errorId || `a11y-${name}-form-error`
    const fieldMessageId = `a11y-${name}-form-message`
    return (
      <Form.Group
        as="fieldset"
        className={`${style == 'button' ? `fs__group--buttons items--${items.length}` : ''} ${
          direction == 'column' ? 'items--column' : 'items--row'
        } ${boxed == true ? 'items--boxed' : ''} ${getFSClassName('formGroupClassName')} ${getFSClassName('formGroupRadioClassName')}`}
        onBlur={onBlur}
      >
        <Form.Label as="legend" className={props.legendClassName ? props.legendClassName : getFSClassName('formLabelClassName')}>
          {legend}
          <FormControls.RequiredLabel required={props.required} />
        </Form.Label>

        {items.length > 0 &&
          items.map((item) => {
            const { checked, controlId, label, onChange } = item
            return (
              <Form.Check key={controlId} className={`${style == 'button' ? 'form-check--button' : ''} ${isInvalid ? fieldErrorClassName : ''}`}>
                <Form.Check.Input
                  type="radio"
                  name={name}
                  id={controlId}
                  aria-required={required}
                  disabled={disabled}
                  readOnly={readonly}
                  checked={checked}
                  isInvalid={isInvalid ? true : undefined}
                  aria-invalid={isInvalid}
                  aria-describedby={isInvalid ? errorMessageId : fieldMessage ? fieldMessageId : undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.checked)}
                  className={getFSClassName('formBooleanInputClassName')}
                />
                {typeof label == 'string' ? (
                  <Form.Check.Label
                    className={getFSClassName('formBooleanLabelClassName')}
                    htmlFor={controlId}
                    dangerouslySetInnerHTML={{ __html: label }}
                  />
                ) : (
                  <Form.Check.Label className={getFSClassName('formBooleanLabelClassName')} htmlFor={controlId}>
                    {label}
                    <FormControls.RequiredLabel {...props} />
                  </Form.Check.Label>
                )}
              </Form.Check>
            )
          })}

        {/* Validation feedback & Field message */}
        {errorMessage && <FormControls.FormErrorText errorMessageId={errorMessageId} errorMessage={errorMessage} />}

        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </Form.Group>
    )
  },
  FormSwitchInGroup: (props: FormSwitchProps) => {
    const { checked, controlId, disabled, extraGroupClasses, fieldMessage, label, onChange } = props
    const fieldMessageId = `a11y-${controlId}-form-message`
    return (
      <Form.Group
        controlId={controlId}
        className={`${getFSClassName('formGroupSwitchClassName')} ${extraGroupClasses || ''}`}
      >
        <Form.Check
          type="switch"
          id={controlId}
          label={
            <>
              {label}
              <FormControls.RequiredLabel {...props} />
            </>
          }
          checked={checked}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.checked)}
          aria-describedby={fieldMessage ? fieldMessageId : undefined}
        />

        {/* Field message */}
        {fieldMessage && <FormControls.FormFieldText fieldMessageId={fieldMessageId} fieldMessage={fieldMessage} />}
      </Form.Group>
    )
  },
  PasswordToggle: (props: FormControlProps) => {
    const { controlId, showPasswordToggle, disabled, readonly } = props
    const passwordToggleId = `a11y-${controlId}-form-password-toggle`
    return (
      <div className="fs__password-toggle">
        <input
          type="checkbox"
          id={passwordToggleId}
          checked={showPasswordToggle?.isChecked}
          disabled={disabled}
          readOnly={readonly}
          onChange={(e) => showPasswordToggle?.onChange(e.currentTarget.checked)}
        />
        <label htmlFor={passwordToggleId} title={showPasswordToggle?.title}>
          <span>{showPasswordToggle?.label}</span>
        </label>
      </div>
    )
  },
  PasswordStrengthBar: (props: { password?: string; passwordScore: PasswordScore; errorMessageId: string }) => {
    const passwordStrengthText = (score: PasswordScore | 'short' | 'none'): string => {
      switch (score) {
        case 'none':
          return ''
        case 'short':
          return i18next.t('auth:tooShort')
        case 'weak':
          return i18next.t('auth:weak')
        case 'almost good':
          return i18next.t('auth:almostGood')
        case 'strong':
          return i18next.t('auth:strong')
      }
    }

    const PasswordStrengthLayout = (score: PasswordScore | 'short' | 'none'): JSX.Element => (
      <div className={`fs__pw-strength pw-strength--${score.replace(/\s+/g, '-')}`}>
        <div className="fs__pw-strength-inner">
          <span className="fs__pw-strength-meter" />
        </div>
        <p id={props.errorMessageId} className="fs__pw-strength-feedback">
          {score != 'none' && `${i18next.t('auth:passwordStrength')}: ${passwordStrengthText(score).toLowerCase()}`}
        </p>
      </div>
    )

    return props.password == undefined
      ? PasswordStrengthLayout('none')
      : props.password.length < 6
      ? PasswordStrengthLayout('short')
      : PasswordStrengthLayout(props.passwordScore)
  },
  ButtonPrimary: (p: ButtonProps) =>
    p.kind == 'button' ? (
      <Button
        type={p.type || 'button'}
        variant="primary"
        className={getFSClassName('clickablePrimaryClassName')}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Button>
    ) : (
      <Link
        to={p.to}
        target={p.target}
        className={`${getFSClassName('clickablePrimaryClassName', 'btn btn-primary')}${p.disabled ? ' disabled' : ''}`}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Link>
    ),
  ButtonSecondary: (p: ButtonProps) =>
    p.kind == 'button' ? (
      <Button
        type={p.type || 'button'}
        variant="secondary"
        className={getFSClassName('clickableSecondaryClassName', 'btn btn-secondary')}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Button>
    ) : (
      <Link
        to={p.to}
        target={p.target}
        className={`${getFSClassName('clickableSecondaryClassName', 'btn btn-secondary')}${
          p.disabled ? ' disabled' : ''
        }`}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Link>
    ),
}

// ==========================================================================
// NOTIFICATIONS
// ==========================================================================

type NotificationStatusTypes = 'error' | 'warning' | 'success' | 'info'

export const FormNotification = (p: {
  message?: string | JSX.Element
  status?: NotificationStatusTypes
  iconId?: string
  extraClasses?: string
}) => {
  const alertErrorClassName = getFSClassName('formAlertErrorClassName', 'alert-danger')
  const alertWarningClassName = getFSClassName('formAlertWarningClassName', 'alert-warning')
  const alertSuccessClassName = getFSClassName('formAlertSuccessClassName', 'alert-success')
  const alertInfoClassName = getFSClassName('formAlertInfoClassName', 'alert-info')
  const alertKindCheck =
    p.status == 'success'
      ? alertSuccessClassName
      : p.status == 'warning'
      ? alertWarningClassName
      : p.status == 'info'
      ? alertInfoClassName
      : alertErrorClassName
  const icon =
    p.status == 'warning' ? '#icon--exclamation-circle' : p.status == 'info' ? '#icon--info-circle' : p.iconId
  return (
    <Alert
      variant={(p.status == 'error' ? 'danger' : p.status) || 'danger'}
      className={`${getFSClassName('formAlertClassName', 'alert small')} ${alertKindCheck} ${p.extraClasses || ''}`}
    >
      {/* {icon && (
        <svg width={24} height={24} aria-hidden="true">
          <use xlinkHref={icon} />
        </svg>
      )} */}
      {typeof p.message == 'string' ? <p dangerouslySetInnerHTML={{ __html: p.message }} /> : <>{p.message}</>}
    </Alert>
  )
}
